/**
 * LoginForm
 *
 * Credential-based login form (currently used only in the landing page when toggled).
 * Uses Formik + Yup for validation and posts to the user-operations mock handler.
 * Shows inline validation and a top-level alert for role-related notifications.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./login.scss";
import PasswordInputComponent from "../../common/components/password-input-component/password-input-component";
import { useSetLoading } from "../../common/hooks/appearance";
import { handleLogin } from "../../common/routines/user-operations";

interface LoginProps {
  setIsShowLoginForm?: (value: boolean) => void;
}

const LoginForm: React.FC<LoginProps> = () => {
  const initialValues = { username: "", password: "", rememberMe: false };
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<any>(null);

  const validationSchema = Yup.object({
    username: Yup.string().max(100).required("Username is required"),
    password: Yup.string().max(30).required("Password is required"),
    rememberMe: Yup.boolean(),
  });

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      handleLogin({
        userDetails: { tes: "test" },
        tenantDetails: { tes: "test" },
      }); // Mocking tenantDetails for now

      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      if (error?.response) {
        setMessage({ message: error?.response?.data?.message, types: "error" });
      } else {
        setMessage({ message: "Something went wrong!", types: "error" });
      }
    }
  };

  useEffect(() => {
    const notification = localStorage.getItem("roleNotification");
    if (notification) {
      setMessage({ message: notification, types: "error" });
      localStorage.removeItem("roleNotification");
    }
  }, []);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, setFieldValue, dirty, isValid }) => (
        <Form className="loginForm">
          <h2>Log In</h2>
          <div className="formBody">
            <div className="field">
              <Field
                type="text"
                name="username"
                placeholder="Username *"
                maxLength={100}
              />
              <ErrorMessage
                name="username"
                component="div"
                className="errorMessage"
              />
            </div>
            <div className="passwordFieldOutlinedInputBox">
              <PasswordInputComponent
                fieldName="password"
                placeholder="Password *"
                optional={true}
                onChange={(e: any) => {
                  setFieldValue("password", e.target.value);
                }}
                maxlength="30"
                className="passwordFieldOutlinedInput"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#F7F7F7",
                    borderWidth: "0px",
                  },
                }}
              />
            </div>
            <button
              type="submit"
              className="submitButton"
              disabled={isSubmitting || isLoading || !dirty || !isValid}
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>
            {message?.message ? (
              <div
                className={
                  message?.types === "error"
                    ? "login-error-message"
                    : "login-success-message"
                }
              >
                {message?.message === "Realm not enabled"
                  ? "This account has been deactivated. To reactivate your account, please contact the AFP team."
                  : message?.message}
              </div>
            ) : (
              ""
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
